import { useMemo, useState } from 'react';
import { Button, Drawer } from '@mantine/core';
import { useIntl } from 'react-intl';

import { AdditionalDetailsStep, AdditionalDetailsStepSummary } from './components/additional-details-step/AdditionalDetailsStep';
import { HandlingUnitsStep, HandlingUnitsStepSummary } from './components/handling-units-step/HandlingUnitsStep';
import { ShipFromStep, ShipFromStepSummary } from './components/ship-from-step/ShipFromStep';
import { ShipToStep, ShipToStepSummary } from './components/ship-to-step/ShipToStep';
import { WizardRatesPanel } from './components/wizard-rates-panel/WizardRatesPanel';
import { WizardStep } from './components/wizard-step/WizardStep';
import { freightBookingWizardMessages } from './freight-booking-wizard-messages';
import styles from './FreightBookingWizard.module.css';
import { hasStepErrors } from './wizard-step-validation';

import type { FreightBookingWizardProps, WizardStepId } from './freight-booking-wizard-types';
import type { RefObject } from 'react';

/**
 * Resolves the `portalRef` prop (a React ref, a raw element, or null/undefined) to
 * the `HTMLElement | undefined` target Mantine's `portalProps.target` expects.
 */
const resolvePortalTarget = (portalRef: RefObject<HTMLElement> | HTMLElement | null | undefined): HTMLElement | undefined => {
  if (portalRef === null || portalRef === undefined) return undefined;
  if ('current' in portalRef) return portalRef.current ?? undefined;
  return portalRef;
};

export const FreightBookingWizard = ({
  isOpen,
  onClose,
  formState,
  onFormChange,
  shipFromOptions,
  states,
  displayWeightUnit,
  onCustomerSelect,
  onAddShipFrom,
  productSuggestions,
  onProductSearch,
  onProductSelect,
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  connectedFreightProviderId,
  selectedRate,
  onSelectRate,
  onRefresh,
  shipFromAddress,
  destinationAddress,
  onSaveDraft,
  onBook,
  isSavingDraft,
  isBooking,
  onDeleteDraft,
  isDeletingDraft,
  canDeleteDraft,
  initialCompletedSteps,
  initialActiveSteps,
  portalRef,
  zIndex,
}: FreightBookingWizardProps) => {
  const intl = useIntl();

  const target = resolvePortalTarget(portalRef);
  // Exempt this portal target as a scroll-lock "shard" so Mantine's Drawer doesn't block
  // scroll on modals (e.g. WWEX registration) sharing this container.
  const removeScrollProps = useMemo(() => (target ? { shards: [target] } : undefined), [target]);

  // Local UI-only state. Selection lives in the container (props), not here.
  // Initialized lazily from the optional pre-completion props (load-saved-quote).
  const [activeSteps, setActiveSteps] = useState<Set<WizardStepId>>(() => new Set(initialActiveSteps ?? (['ship-from'] as WizardStepId[])));
  const [completedSteps, setCompletedSteps] = useState<Partial<Record<WizardStepId, boolean>>>(() => initialCompletedSteps ?? {});
  const [visitedSteps, setVisitedSteps] = useState<Set<WizardStepId>>(
    () => new Set(initialActiveSteps ?? (['ship-from'] as WizardStepId[])),
  );

  const handleCompleteStep = (step: WizardStepId) => {
    setCompletedSteps((prev) => ({ ...prev, [step]: true }));
    setActiveSteps((prev) => {
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
  };

  const handleEditStep = (step: WizardStepId) => {
    setVisitedSteps((prev) => new Set([...prev, step]));
    setActiveSteps((prev) => new Set([...prev, step]));
  };

  const isWarningStep = (step: WizardStepId) => visitedSteps.has(step) && !activeSteps.has(step) && hasStepErrors(step, formState);

  const canBook = Boolean(selectedRate) && Boolean(formState.destinationAddress.name) && Boolean(formState.destinationAddress.phone);

  const stepProps = { formState, onFormChange };

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header }}
      data-ignore-root-focus
      onClose={onClose}
      opened={isOpen}
      padding={0}
      position='right'
      portalProps={target ? { target } : undefined}
      removeScrollProps={removeScrollProps}
      size='100%'
      title={intl.formatMessage(freightBookingWizardMessages.title)}
      withinPortal={target !== undefined}
      zIndex={zIndex}
    >
      <div className={styles.drawerBody}>
        <div className={styles.stepsColumn}>
          <div className={styles.steps}>
            <WizardStep
              isActive={activeSteps.has('ship-from')}
              isCompleted={Boolean(completedSteps['ship-from'])}
              isWarning={isWarningStep('ship-from')}
              onCollapse={() => handleCompleteStep('ship-from')}
              onEdit={() => handleEditStep('ship-from')}
              stepNumber={1}
              summary={<ShipFromStepSummary formState={formState} shipFromOptions={shipFromOptions} />}
              title={intl.formatMessage(freightBookingWizardMessages.shipFromStepTitle)}
            >
              <ShipFromStep {...stepProps} onAddShipFrom={onAddShipFrom} shipFromOptions={shipFromOptions} />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('ship-to')}
              isCompleted={Boolean(completedSteps['ship-to'])}
              isWarning={isWarningStep('ship-to')}
              onCollapse={() => handleCompleteStep('ship-to')}
              onEdit={() => handleEditStep('ship-to')}
              stepNumber={2}
              summary={<ShipToStepSummary formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.shipToStepTitle)}
            >
              <ShipToStep {...stepProps} onCustomerSelect={onCustomerSelect} states={states} />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('handling-units')}
              isCompleted={Boolean(completedSteps['handling-units'])}
              isWarning={isWarningStep('handling-units')}
              onCollapse={() => handleCompleteStep('handling-units')}
              onEdit={() => handleEditStep('handling-units')}
              stepNumber={3}
              summary={<HandlingUnitsStepSummary displayWeightUnit={displayWeightUnit} formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.handlingUnitsStepTitle)}
            >
              <HandlingUnitsStep
                {...stepProps}
                displayWeightUnit={displayWeightUnit}
                onProductSearch={onProductSearch}
                onProductSelect={onProductSelect}
                productSuggestions={productSuggestions}
              />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('additional-details')}
              isCompleted={Boolean(completedSteps['additional-details'])}
              isWarning={isWarningStep('additional-details')}
              onCollapse={() => handleCompleteStep('additional-details')}
              onEdit={() => handleEditStep('additional-details')}
              stepNumber={4}
              summary={<AdditionalDetailsStepSummary formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.additionalDetailsStepTitle)}
            >
              <AdditionalDetailsStep {...stepProps} />
            </WizardStep>
          </div>
        </div>
        <div className={styles.ratesColumn}>
          <WizardRatesPanel
            connectedFreightProviderId={connectedFreightProviderId}
            destinationAddress={destinationAddress}
            insurance={formState.insurance}
            isLoadingRates={isLoadingRates}
            onInsuranceChange={(next) => onFormChange({ ...formState, insurance: next })}
            onRefresh={onRefresh}
            onSelectRate={onSelectRate}
            rateError={rateError}
            rateErrorCode={rateErrorCode}
            rates={rates}
            selectedRate={selectedRate}
            shipFromAddress={shipFromAddress}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose} variant='ghost'>
          {intl.formatMessage(freightBookingWizardMessages.cancel)}
        </Button>
        <div className={styles.footerRight}>
          {canDeleteDraft && (
            <Button color='destructive' disabled={isDeletingDraft} loading={isDeletingDraft} onClick={onDeleteDraft}>
              {intl.formatMessage(freightBookingWizardMessages.deleteDraft)}
            </Button>
          )}
          <Button disabled={isSavingDraft} loading={isSavingDraft} onClick={onSaveDraft} variant='tertiary'>
            {intl.formatMessage(freightBookingWizardMessages.saveDraft)}
          </Button>
          <Button color='green' disabled={!canBook} loading={isBooking} onClick={onBook}>
            {intl.formatMessage(freightBookingWizardMessages.bookShipment)}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
